import { Fragment, useEffect, useState } from 'react';
import { IGlobalHistory, IShipmentData } from '../../../../../../../types/orders';
import { formatShortDateAndHour } from '../../../../../../../utils/formatDatesAndHours';
import ShipmentTrackingData from '../ShipmentTrackingData/ShipmentTrackingData';

import './TrackingAndDatesSection.scss';

interface IDateProperties {
  nameDate: string;
  valueDate?: string | null;
  key?: string;
}

interface ITrackingAndDatesSectionProps {
  shipmentData: IShipmentData;
  countryTimeZone?: number;
  setShowProcessModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEditedProcessModal: React.Dispatch<React.SetStateAction<boolean>>;
  /** Abre el flujo de edición del paso Preparación → Tránsito a bodega (motivos + modal de transición). */
  onEditInboundTransitClick?: () => void;
}

/**
 * Lista de fechas de tracking del envío e historial; el ícono en «Tránsito a bodega» delega en `onEditInboundTransitClick`.
 */
const TrackingAndDatesSection = ({
  shipmentData,
  countryTimeZone,
  setShowProcessModal,
  setIsEditedProcessModal,
  onEditInboundTransitClick
}: ITrackingAndDatesSectionProps) => {
  const [datesList, setDatesList] = useState<IDateProperties[]>([]);
  const { shipmentStatusDates, trackingInfo, globalHistory } = shipmentData;
  const deliveryDate = trackingInfo?.deliveryDate;
  const {
    pending,
    confirmed,
    preparing,
    inbound,
    inWarehouse,
    deliveryProcess,
    transitToDestination,
    cancelled
  } = shipmentStatusDates;

  const shipmentDateKeysWithTrackingUi = ['deliveryProcess', 'transitToDestination', 'transitToWarehouse'];

  const renderTrackingStepKey = (key: string) => {
    if (key === 'deliveryProcess') return 'deliveryProcess';
    if (key === 'transitToDestination') return 'transitToDestination';
    if (key === 'transitToWarehouse') return 'transitToWarehouse';
    return undefined;
  };

  const generateCopyItem = (historyType: 'edition' | 'report', platform?: 'NODI' | 'SOGA') => {
    const isReport = historyType === 'report';
    const nodiEdition = historyType === 'edition' && platform === 'NODI';
    const sogaEdition = historyType === 'edition' && platform === 'SOGA';
    if (isReport) return 'Correo enviado - error en información:';
    if (nodiEdition) return 'Información del envío actualizado desde Nodi';
    if (sogaEdition) return 'Información del envío actualizado desde Soga';
  };

  const globalHistoryFormat = (dates: IGlobalHistory[]) => {
    const formatDates: IDateProperties[] = [];
    for (const date of dates) {
      if (date.createdAt) {
        const newDate = {
          nameDate: generateCopyItem(date?.typeLog, date?.platform) || '',
          valueDate: date?.createdAt
        };
        if (newDate?.nameDate && newDate?.valueDate) {
          formatDates.push(newDate);
        }
      }
    }
    return formatDates;
  };

  const unifyDates = () => {
    const trakingDates: IDateProperties[] = [
      { nameDate: 'Cancelado', valueDate: cancelled },
      { nameDate: 'Entregado', valueDate: deliveryDate },
      {
        nameDate: 'Tránsito al destino',
        valueDate: transitToDestination,
        key: 'transitToDestination'
      },
      {
        nameDate: 'Proceso de entrega',
        valueDate: deliveryProcess,
        key: 'deliveryProcess'
      },
      { nameDate: 'En bodega', valueDate: inWarehouse},
      { nameDate: 'Tránsito a bodega', valueDate: inbound, key: 'transitToWarehouse' },
      { nameDate: 'Preparación', valueDate: preparing },
      { nameDate: 'Confirmado', valueDate: confirmed },
      { nameDate: 'Por confirmar', valueDate: pending }
    ];
    const filterTrakingDates: IDateProperties[] = trakingDates.filter(trakingDate => !!trakingDate?.valueDate);
    const historyDates = globalHistoryFormat(globalHistory || []);
    const unionOfLists: IDateProperties[] = [...historyDates, ...filterTrakingDates];
    const orderList = unionOfLists.sort(
      (a: IDateProperties, b: IDateProperties) =>
        new Date(b?.valueDate || '').getTime() - new Date(a?.valueDate || '').getTime()
    );
    setDatesList([...orderList]);
  };

  useEffect(() => {
    unifyDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentData]);

  return (
    <div className="trackingAndDatesSection">
      {/* This is the date that the ops user selected in the date picker as the delivery date for a shipment, not the actual date that the shipment was moved to the "Entregado" status */}
      {datesList &&
        datesList.length > 0 &&
        datesList.map((dateInfo, index) => {
          const showShipmentTrackingBlock =
            !!dateInfo.valueDate &&
            !!dateInfo.key &&
            shipmentDateKeysWithTrackingUi.includes(dateInfo.key);

          return (
            <Fragment key={`order-items-${dateInfo?.nameDate}-${index}`}>
              <div
                className={`${showShipmentTrackingBlock ? 'deliveryProcessBlock' : ''}`}
              >
                {dateInfo?.valueDate && (
                  <div
                    className={`logisticsDate ${index === 0 ? 'active' : ''} ${dateInfo.key === 'transitToWarehouse' ? 'flex items-center gap-2' : ''}`}
                  >
                    {dateInfo.nameDate} : {formatShortDateAndHour(dateInfo?.valueDate || '')?.textWithTimezone}
                  </div>
                )}
                {showShipmentTrackingBlock && (
                  <ShipmentTrackingData
                    shipmentData={shipmentData}
                    countryTimeZone={countryTimeZone}
                    setShowProcessModal={setShowProcessModal}
                    setIsEditedProcessModal={setIsEditedProcessModal}
                    trackingStepKey={renderTrackingStepKey(dateInfo.key || '')}
                    onEditInboundTransitClick={onEditInboundTransitClick}
                  />
                )}
              </div>
            </Fragment>
          );
        })}
    </div>
  );
};

export default TrackingAndDatesSection;
